import type { EngagementLevel, InteractionEvent, Item, Session, User } from '@app/core';

import type { AppDatabase } from './database.js';
import type {
  CreateInteractionEvent,
  CreateItemInput,
  InteractionRepository,
  ItemRepository,
  SessionRepository,
  SessionSummary,
  UserRepository,
} from './repositories.js';

function generateId(): string {
  return crypto.randomUUID();
}

export class DexieItemRepository implements ItemRepository {
  constructor(private db: AppDatabase) {}

  async getAll(): Promise<Item[]> {
    return this.db.items.toArray();
  }

  async getActive(): Promise<Item[]> {
    return this.db.items.where('status').anyOf('active', 'fading').toArray();
  }

  async getById(id: string): Promise<Item | null> {
    return (await this.db.items.get(id)) ?? null;
  }

  async create(input: CreateItemInput): Promise<Item> {
    const item: Item = {
      id: generateId(),
      text: input.text,
      rawInput: input.rawInput,
      source: input.source,
      category: input.category,
      consequence: input.consequence,
      effort: input.effort,
      curve: input.curve,
      recurrence: input.recurrence,
      status: 'active',
      createdAt: new Date(),
      reheatCount: 0,
    };
    await this.db.items.add(item);
    return item;
  }

  async update(id: string, changes: Partial<Item>): Promise<Item> {
    await this.db.items.update(id, changes);
    const updated = await this.db.items.get(id);
    if (!updated) throw new Error(`Item ${id} not found`);
    return updated;
  }
}

export class DexieSessionRepository implements SessionRepository {
  constructor(private db: AppDatabase) {}

  async open(engagementLevel: EngagementLevel): Promise<Session> {
    const session: Session = {
      id: generateId(),
      openedAt: new Date(),
      engagementLevel,
      itemsSurfaced: 0,
      itemsActedOn: 0,
      itemsSnoozed: 0,
      itemsDismissed: 0,
    };
    await this.db.sessions.add(session);
    return session;
  }

  async close(id: string, summary: SessionSummary): Promise<void> {
    await this.db.sessions.update(id, {
      closedAt: new Date(),
      ...summary,
    });
  }

  async getLast(): Promise<Session | null> {
    const sessions = await this.db.sessions.orderBy('openedAt').reverse().limit(1).toArray();
    return sessions[0] ?? null;
  }
}

export class DexieInteractionRepository implements InteractionRepository {
  constructor(private db: AppDatabase) {}

  async log(event: CreateInteractionEvent): Promise<void> {
    const full: InteractionEvent = {
      id: generateId(),
      ...event,
    };
    await this.db.interactions.add(full);
  }

  async getByItem(itemId: string): Promise<InteractionEvent[]> {
    return this.db.interactions.where('itemId').equals(itemId).toArray();
  }
}

export class DexieUserRepository implements UserRepository {
  constructor(private db: AppDatabase) {}

  async get(): Promise<User | null> {
    const users = await this.db.users.toArray();
    return users[0] ?? null;
  }

  async create(): Promise<User> {
    const user: User = {
      id: generateId(),
      hasOnboarded: false,
      createdAt: new Date(),
    };
    await this.db.users.add(user);
    return user;
  }

  async update(changes: Partial<User>): Promise<User> {
    const user = await this.get();
    if (!user) throw new Error('No user found');
    await this.db.users.update(user.id, changes);
    const updated = await this.db.users.get(user.id);
    if (!updated) throw new Error('User not found after update');
    return updated;
  }
}
